/**
 * PIN 보호 서비스
 * PIN 해싱, 검증, 이중 암호화 처리
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { encryptData, decryptData } from './webCryptoService';

const COLLECTION_NAME = 'secureNotes';
const PIN_ITERATIONS = 100000;

/**
 * PIN을 PBKDF2로 해싱
 */
export async function hashPin(pin, salt = null) {
  try {
    const encoder = new TextEncoder();
    
    // Salt 생성 또는 사용
    const useSalt = salt || crypto.getRandomValues(new Uint8Array(16));
    const saltStr = salt ? salt : btoa(String.fromCharCode(...useSalt));
    
    // PIN을 키 재료로 변환
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(pin),
      'PBKDF2',
      false,
      ['deriveBits']
    );
    
    // PBKDF2로 해싱
    const hashBuffer = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: encoder.encode(saltStr),
        iterations: PIN_ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      256
    );
    
    const hashStr = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
    
    return {
      hash: hashStr,
      salt: saltStr
    };
  } catch (error) {
    console.error('Error hashing PIN:', error);
    throw error;
  }
}

/**
 * PIN 검증
 */
export async function verifyPin(inputPin, storedHash, storedSalt) {
  try {
    const { hash } = await hashPin(inputPin, storedSalt);
    return hash === storedHash;
  } catch (error) {
    console.error('Error verifying PIN:', error);
    throw error;
  }
}

/**
 * 메모에 PIN 설정
 */
export async function setNotePin(noteId, userId, pin) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    if (docSnap.data().userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    // PIN 해싱
    const { hash, salt } = await hashPin(pin);
    
    // 기존 콘텐츠 재암호화 (PIN 키 추가)
    const noteData = docSnap.data();
    if (noteData.content) {
      // 먼저 복호화
      const decryptedContent = await decryptData(noteData.content, userId);
      // PIN으로 추가 암호화
      const pinEncrypted = await encryptWithPin(decryptedContent, pin, salt);
      
      await updateDoc(docRef, {
        content: pinEncrypted,
        isPinProtected: true,
        pinHash: hash,
        pinSalt: salt,
        updatedAt: new Date()
      });
    } else {
      // 콘텐츠가 없으면 PIN 정보만 저장
      await updateDoc(docRef, {
        isPinProtected: true,
        pinHash: hash,
        pinSalt: salt,
        updatedAt: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error setting PIN:', error);
    throw error;
  }
}

/**
 * PIN 변경
 */
export async function changeNotePin(noteId, userId, oldPin, newPin) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    const noteData = docSnap.data();
    
    if (noteData.userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    if (!noteData.isPinProtected) {
      throw new Error('Note is not PIN protected');
    }
    
    // 기존 PIN 검증
    const isValid = await verifyPin(oldPin, noteData.pinHash, noteData.pinSalt);
    if (!isValid) {
      throw new Error('Invalid PIN');
    }
    
    // 새 PIN 해싱
    const { hash, salt } = await hashPin(newPin);
    
    // 콘텐츠 재암호화
    if (noteData.content) {
      // 기존 PIN으로 복호화
      const decrypted = await decryptWithPin(noteData.content, oldPin, noteData.pinSalt);
      // 새 PIN으로 암호화
      const reEncrypted = await encryptWithPin(decrypted, newPin, salt);
      
      await updateDoc(docRef, {
        content: reEncrypted,
        pinHash: hash,
        pinSalt: salt,
        updatedAt: new Date()
      });
    } else {
      await updateDoc(docRef, {
        pinHash: hash,
        pinSalt: salt,
        updatedAt: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error changing PIN:', error);
    throw error;
  }
}

/**
 * PIN 제거
 */
export async function removeNotePin(noteId, userId, currentPin) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    const noteData = docSnap.data();
    
    if (noteData.userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    if (!noteData.isPinProtected) {
      return true; // 이미 PIN이 없음
    }
    
    // PIN 검증
    const isValid = await verifyPin(currentPin, noteData.pinHash, noteData.pinSalt);
    if (!isValid) {
      throw new Error('Invalid PIN');
    }
    
    // 콘텐츠 복호화 (PIN 제거)
    if (noteData.content) {
      const decrypted = await decryptWithPin(noteData.content, currentPin, noteData.pinSalt);
      // 일반 암호화로 재암호화
      const reEncrypted = await encryptData(decrypted, userId);
      
      await updateDoc(docRef, {
        content: reEncrypted,
        isPinProtected: false,
        pinHash: null,
        pinSalt: null,
        updatedAt: new Date()
      });
    } else {
      await updateDoc(docRef, {
        isPinProtected: false,
        pinHash: null,
        pinSalt: null,
        updatedAt: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error removing PIN:', error);
    throw error;
  }
}

/**
 * PIN으로 암호화
 */
async function encryptWithPin(data, pin, salt) {
  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    
    // PIN에서 키 생성
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(pin),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt),
        iterations: PIN_ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );
    
    // IV 생성
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // 암호화
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoder.encode(data)
    );
    
    // IV와 암호화된 데이터 결합
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    console.error('Error encrypting with PIN:', error);
    throw error;
  }
}

/**
 * PIN으로 복호화
 */
async function decryptWithPin(encryptedData, pin, salt) {
  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    
    // Base64 디코딩
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    
    // IV와 암호화된 데이터 분리
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    
    // PIN에서 키 생성
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(pin),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    
    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt),
        iterations: PIN_ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );
    
    // 복호화
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );
    
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Error decrypting with PIN:', error);
    throw error;
  }
}

/**
 * PIN 보호된 메모 접근
 */
export async function accessPinProtectedNote(noteId, userId, pin) {
  try {
    const docRef = doc(db, COLLECTION_NAME, noteId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Note not found');
    }
    
    const noteData = docSnap.data();
    
    if (noteData.userId !== userId) {
      throw new Error('Unauthorized access');
    }
    
    if (!noteData.isPinProtected) {
      return noteData; // PIN 보호 없음
    }
    
    // PIN 검증
    const isValid = await verifyPin(pin, noteData.pinHash, noteData.pinSalt);
    if (!isValid) {
      throw new Error('Invalid PIN');
    }
    
    // 콘텐츠 복호화
    if (noteData.content) {
      const decrypted = await decryptWithPin(noteData.content, pin, noteData.pinSalt);
      return {
        ...noteData,
        content: decrypted
      };
    }
    
    return noteData;
  } catch (error) {
    console.error('Error accessing PIN protected note:', error);
    throw error;
  }
}